import React, { useEffect, useState } from "react";
import { SecondaryButton, UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import Link from "next/link";
import { useRouter } from "next/router";
import { BasketOutlined as ShoppingWithUsIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/BasketOutlined";
import { DeliveryOutlined as DeliveryIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/DeliveryOutlined";
import { GiftCardOutlined as GiftCardIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GiftCardOutlined";
import { HomeOutlined as HomeIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/HomeOutlined";
import { InStoreOutlined as InStoreIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/InStoreOutlined";
import { OrdersOutlined as OrdersIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/OrdersOutlined";
import { ReturnsAndRefundsOutlined as RefundsIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ReturnsAndRefundsOutlined";
import GuaranteesAndTroubleshootingIcon from "../assets/icons/guarantees.svg";
import analyticsTrigger from "@/utils/analytics/trigger/analyticsTrigger";
import { useExperience, extractExperiences, trackMonetateEvent, findExperience } from "../context/MonetateContext";
import styles from "./index.module.scss";
import { Badge } from "@JohnLewisPartnership/jl-design-system/dist/components/badge/Badge";
import { ChevronRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import { isFeatureEnabled } from "@/utils/feature-flags";

export const metadata = {
  title: `Customer Services`,
  description: `Find quick answers to your questions about orders, deliveries, returns, refunds, services and more. Get the help you need, fast, from our customer service hub.`,
};

const ab6017ExperienceName = "GH | AB-6017 |  AB/ABn Test |  C&A  | Add SS shortcuts to CS page | Mobile and Desktop ";

export const getServerSideProps = async ctx => {
  const experiences = extractExperiences(ctx.req);
  const isSelfServeShortcutsFlagEnabled = await isFeatureEnabled(ctx, "self_serve_shortcuts");
  const isFaqContentRemoved = await isFeatureEnabled(ctx, "is_faq_content_removed");
  // Store impressionId for AB-6017 experiment in cookie
  const ab6017Experience = findExperience(experiences, ab6017ExperienceName);

  if (ab6017Experience?.impressionId && ab6017Experience?.variantLabel === "Experiment") {
    // Get the hostname from the request to set the correct domain
    const hostname = ctx.req.headers.host || "";

    // Extract the base domain (e.g., .johnlewis.com, .project4.com)
    // For integration.project4.com -> .project4.com
    // For www.johnlewis.com -> .johnlewis.com
    let cookieDomain = "";
    if (hostname.includes("johnlewis.com")) {
      cookieDomain = "; Domain=.johnlewis.com";
    } else if (hostname.includes("project4.com")) {
      cookieDomain = "; Domain=.project4.com";
    }
    // For localhost or other domains, don't set Domain attribute (defaults to current host)

    // Set session cookie (no Max-Age = expires when browser closes)
    ctx.res.setHeader(
      "Set-Cookie",
      `monetate_impression_AB6017=${ab6017Experience.impressionId}${cookieDomain}; Path=/; HttpOnly; Secure; SameSite=Lax`,
    );
  }

  return {
    props: {
      experiences,
      isSelfServeShortcutsFlagEnabled,
      isFaqContentRemoved,
    },
  };
};
function Index({ isSelfServeShortcutsFlagEnabled, isFaqContentRemoved }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const router = useRouter();

  const selfServeShortcutsExperience = useExperience(ab6017ExperienceName);

  const isSelfServeShortcutsExperiment = selfServeShortcutsExperience?.variantLabel === "Experiment";

  const showSelfServeShortcuts = isSelfServeShortcutsFlagEnabled && isSelfServeShortcutsExperiment;

  useEffect(() => {
    window.auth0?.isAuthenticated().then(setIsLoggedIn);
    analyticsTrigger("dataStateChange", router.pathname, undefined, "pageload");
  }, []);

  const handleGetInTouchClick = () => {
    if (isSelfServeShortcutsExperiment) {
      handleGeneralEnquiryClick();
    }

    router.push("/contact-us");
  };

  const handleGeneralEnquiryClick = () => {
    trackMonetateEvent("AB-6017-click-any-CS-CTA");
  };

  return (
    <>
      <h1 className="page-heading">Customer services</h1>
      <div className="util-dividerContainer">
        <h2 className="util-dividerWithText">How can we help?</h2>
      </div>
      <p className={styles.landingPage__headerMessage}>
        To direct you to the best contact, please choose one of the options below
      </p>
      {showSelfServeShortcuts && (
        <div className={styles.selfServeShortcuts}>
          <ul onClick={handleGeneralEnquiryClick}>
            <li>
              <Link
                href={`${process.env.NEXT_PUBLIC_ACCOUNT_DOMAIN}/track-order`}
                className={styles.landingPage__quickLink}
                data-testid="qa-track-order"
              >
                <div className={styles.shortcutName}>
                  <Badge badgeType="--success" label="Quick action" />
                  <span>Track your order</span>
                </div>
                <ChevronRightOutlined />
              </Link>
            </li>
            <li>
              <Link
                href="/returns?dt=returningAnItem-small-notWanted-online"
                className={styles.landingPage__quickLink}
                data-testid="qa-return-item"
              >
                <div className={styles.shortcutName}>
                  <Badge badgeType="--success" label="Quick action" />
                  <span>Return a small item</span>
                </div>
                <ChevronRightOutlined />
              </Link>
            </li>
            <li>
              <Link
                href="/returns?dt=trackReturnAndRefund"
                className={styles.landingPage__quickLink}
                data-testid="qa-track-return"
              >
                <div className={styles.shortcutName}>
                  <Badge badgeType="--success" label="Quick action" />
                  <span>Track a return and refund</span>
                </div>
                <ChevronRightOutlined />
              </Link>
            </li>
          </ul>
        </div>
      )}
      <div className={styles.helpTopicsContainer}>
        <ul onClick={handleGeneralEnquiryClick}>
          <li>
            <Link href="/delivery-information" data-testid="cs-delivery">
              <DeliveryIcon size="large" />
              <span>Delivery & collection</span>
            </Link>
          </li>
          <li>
            <Link href="/returns" data-testid="cs-returns">
              <RefundsIcon size="large" />
              <span>Returns & refunds</span>
            </Link>
          </li>
          <li>
            <Link href="/prices-and-payment" data-testid="cs-payments">
              <GiftCardIcon size="large" />
              <span>Payments & gift cards</span>
            </Link>
          </li>
          <li>
            <Link href="/orders" data-testid="cs-your-orders">
              <OrdersIcon size="large" />
              <span>Your orders</span>
            </Link>
          </li>
          <li>
            <Link href="/troubleshooting-guides" data-testid="cs-guarantees">
              <img src={GuaranteesAndTroubleshootingIcon.src} alt="Guarantees & Troubleshooting" />
              <span>Guarantees and Troubleshooting</span>
            </Link>
          </li>
          <li>
            <Link href="https://www.johnlewis.com/our-services" data-testid="cs-services">
              <HomeIcon size="large" />
              <span>In store & home services</span>
            </Link>
          </li>
          <li>
            <Link href="https://www.johnlewis.com/our-shops" data-testid="cs-shop-finder">
              <InStoreIcon size="large" />
              <span>Shop finder</span>
            </Link>
          </li>
          <li>
            <Link href="/shopping-with-us" data-testid="cs-shopping">
              <ShoppingWithUsIcon size="large" />
              <span>Shopping with us</span>
            </Link>
          </li>
        </ul>
      </div>
      <div className="util-container">
        <div className="util-dividerContainer">
          <h2 className="util-dividerWithText">Get in touch?</h2>
        </div>
        <div className={styles.landingPage__contactUsOptionsContainer}>
          <div className={styles.contactUsOption}>
            <h2>Help with my electrical product</h2>
            <p>Search our hub for guides on how to use or fix your electrical product.</p>
            <SecondaryButton
              tag="a"
              href={`${process.env.NEXT_PUBLIC_TV_AND_LAPTOP_SUPPORT_URL}?origin=Web&referrer=CustomerService`}
              data-testid="cs-electrical"
              onClick={handleGeneralEnquiryClick}
            >
              Get Support
            </SecondaryButton>
          </div>
          <div className={styles.contactUsOption}>
            <h2>Get an update on your case</h2>
            <p>You can check the status of your customer services case in your online account area.</p>
            <Link href="https://account.johnlewis.com/cases">
              <SecondaryButton data-testid="cs-case" onClick={handleGeneralEnquiryClick}>
                {isLoggedIn ? "Check status" : "Sign in to check status"}
              </SecondaryButton>
            </Link>
          </div>
          <div className={styles.contactUsOption}>
            <h2>Contact us</h2>
            <p>We’ll put you in touch with the right team to help.</p>
            <SecondaryButton onClick={handleGetInTouchClick} data-testid="cs-contact">
              Get in touch
            </SecondaryButton>
          </div>
        </div>
        <div className={styles.landingPage__faqLinkContainer}>
          {!isFaqContentRemoved && (
            <Link href="/faq">
              <UnderlinedButton>Can't find what you are looking for? Try FAQs</UnderlinedButton>
            </Link>
          )}
        </div>
      </div>
    </>
  );
}

Index.metadata = metadata;

export default Index;
